import { ReducerCreators } from '@reduxjs/toolkit';

import { StylepacksState } from './types';
import GetStylepacks from '@/connection/greenroom/get-stylepacks';
import { FetchError } from '@/connection/helpers';
import { StyleCategories, StylepackCategoryMap, type Stylepack } from '@/types/stylepacks.types';

const VIDEO_PROVIDER = 'capsule';
const AUDIO_PROVIDER = 'epidemic sound';
const ALLOWED_PROVIDERS = [VIDEO_PROVIDER, AUDIO_PROVIDER];

export const reducers = (create: ReducerCreators<StylepacksState>) => ({
  loadStylepacks: create.asyncThunk(
    async (_, { rejectWithValue }) => {
      try {
        return GetStylepacks();
      } catch (e) {
        if (e instanceof FetchError) {
          throw rejectWithValue(e.body);
        }

        throw rejectWithValue({ error: e });
      }
    },
    {
      fulfilled: (state, action) => {
        const packs = action.payload;

        const { stylepacks, stylepackAudio } = packs.templates.reduce<{
          stylepacks: Record<string, Stylepack>;
          stylepackAudio: Record<string, string>;
        }>(
          (acc, pack) => {
            // packs request contains hundreds of rows with data not relevant to this app
            // see network response for full response body
            if (!ALLOWED_PROVIDERS.includes(pack.provider)) {
              return acc;
            }

            if (pack.provider === VIDEO_PROVIDER) {
              // group rows by which stylepack they belong to
              if (acc.stylepacks[pack.stylePack]) {
                acc.stylepacks[pack.stylePack].videos.push(pack);
              } else {
                acc.stylepacks[pack.stylePack] = {
                  stylePack: pack.stylePack,
                  themeId: pack.themeId ?? null,
                  name: pack.name,
                  videos: [pack],
                  categories: StylepackCategoryMap[pack.stylePack] || [StyleCategories.classic],
                };
              }
            } else if (pack.videoUrl) {
              acc.stylepackAudio[pack.stylePack] = pack.videoUrl; // it's actually an mp3 link - idk
            }

            return acc;
          },
          { stylepacks: {}, stylepackAudio: {} }
        );

        state.stylepacks = Object.values(stylepacks);
        state.stylepackAudio = stylepackAudio;
        state.errorLoadingStylepacks = false;
        state.stylepackContent = action.payload.templates;
      },
      pending: (state) => {
        state.loadingStylepacks = true;
      },
      rejected: (state, action) => {
        console.error(action.payload);
        state.errorLoadingStylepacks = true;
      },
      settled: (state) => {
        state.loadingStylepacks = false;
      },
    }
  ),
});
